/**
 * XCS Import utilities
 * Parse xTool Creative Space (.xcs) files into TemplateShapes
 */
import type { TemplateShape, OriginalXcsGlyphData } from '../types/dbTypes';
import type { ImportResult } from './svgImport';

// ============================================================
// XCS Layer Colors (for operation mapping)
// ============================================================
const XCS_COLOR_RED = '#fe0002';
const XCS_COLOR_BLACK = '#000000';
const XCS_COLOR_CYAN = '#00befe';

// ============================================================
// XCS File Structure Interfaces (for parsing)
// ============================================================

interface XcsTransform {
    x: number;
    y: number;
}

interface XcsGlyphData {
    dPath: string;
    advanceWidth: number;
    advanceHeight: number;
    leftBearing: number;
    topBearing: number;
    bbox: {
        minX: number;
        minY: number;
        maxX: number;
        maxY: number;
    };
}

interface XcsStroke {
    visible?: boolean;
    width?: number;
    color?: number;
    cap?: string;
    join?: string;
}

interface XcsFill {
    visible?: boolean;
    color?: number;
}

interface XcsShape {
    id: string;
    type: 'RECT' | 'PATH' | 'TEXT' | 'CIRCLE' | 'ELLIPSE' | 'LINE' | 'STAR' | 'REGULAR_POLYGON' | 'BITMAP' | string;
    x: number;
    y: number;
    width: number;
    height: number;
    angle?: number;
    scale?: XcsTransform;
    zOrder?: number;  // Layer stacking order (higher = front)
    layerTag?: string;
    layerColor?: string;
    isFill?: boolean;
    stroke?: XcsStroke;
    fill?: XcsFill;
    // RECT specific
    radius?: number;  // corner radius for rects
    // PATH specific
    dPath?: string;
    fillRule?: 'nonzero' | 'evenodd';
    graphicX?: number;
    graphicY?: number;
    isCompoundPath?: boolean;
    // TEXT specific
    text?: string;
    style?: {
        fontSize?: number;
        fontFamily?: string;
        align?: 'left' | 'center' | 'right';
    };
    fontData?: {
        fontInfo?: {
            unitsPerEm?: number;
            ascent?: number;
            descent?: number;
            lineHeight?: number;
        };
        glyphData?: Record<string, XcsGlyphData>;
    };
    // CIRCLE/ELLIPSE specific
    // In xTool: x,y is TOP-LEFT, uses width/height (not radius)
    // ELLIPSE specific (if radiusX/radiusY are present)
    radiusX?: number;
    radiusY?: number;
    // LINE specific
    points?: { x: number; y: number }[];
    // STAR specific
    // In xTool: x,y is TOP-LEFT, uses width/height/count/starInnerScale
    count?: number;           // number of star points (or polygon sides)
    starInnerScale?: number;  // inner radius as ratio of outer (0-1)
    // Legacy properties (for compatibility)
    outerRadius?: number;
    innerRadius?: number;
    numPoints?: number;
    sides?: number;
    // BITMAP specific
    base64?: string;  // Base64 data URI of the image (includes data:image/png;base64, prefix)
    visible?: boolean;
    left?: number;    // BITMAP may use left instead of x (fallback)
    top?: number;     // BITMAP may use top instead of y (fallback)
    lock?: boolean;
    originWidth?: number;  // Original image pixel width
    originHeight?: number; // Original image pixel height
}

interface XcsCanvasPanel {
    id: string;
    displays: XcsShape[];
}

interface XcsProcessingParameter {
    VECTOR_CUTTING?: {
        parameter?: {
            customize?: {
                power?: number;
                speed?: number;
                repeat?: number;
                processingLightSource?: string;
                // Kerf compensation
                enableKerf?: boolean;
                kerfDistance?: number;
                // Break point settings (segmented cutting)
                enableBreakPoint?: boolean;
                breakPointSize?: number;
                breakPointCount?: number;
                breakPointMode?: string;
                breakPointDistance?: number;
                breakPointPower?: number;
            };
        };
    };
    VECTOR_ENGRAVING?: {
        parameter?: {
            customize?: {
                power?: number;
                speed?: number;
                repeat?: number;
                processingLightSource?: string;
                // Kerf compensation
                enableKerf?: boolean;
                kerfDistance?: number;
            };
        };
    };
    FILL_VECTOR_ENGRAVING?: {
        parameter?: {
            customize?: {
                power?: number;
                speed?: number;
                repeat?: number;
                density?: number;
                bitmapScanMode?: string;
                processingLightSource?: string;
                // Bitmap engraving settings
                bitmapEngraveMode?: string;
                dotDuration?: number;
            };
        };
    };
    BITMAP_ENGRAVING?: {
        parameter?: {
            customize?: {
                power?: number;
                speed?: number;
                repeat?: number;
                dpi?: number;  // BITMAP uses dpi instead of density
                bitmapScanMode?: string;
                processingLightSource?: string;
                bitmapEngraveMode?: string;
                dotDuration?: number;
            };
        };
    };
}

interface XcsDeviceDisplay {
    processingType?: string;
    processIgnore?: boolean;
    isFill?: boolean;
    data?: XcsProcessingParameter;
}

// XCS uses Map-like structure for some data
interface XcsMapLike<T> {
    dataType: 'Map';
    value: [string, T][];
}

interface XcsCanvasData {
    mode: string;
    data: Record<string, unknown>;
    displays: XcsMapLike<XcsDeviceDisplay>;
}

interface XcsDevice {
    extId?: string;  // Device identifier (e.g., "F1", "P2", "D1")
    data?: XcsMapLike<XcsCanvasData>;
}

interface XcsFile {
    canvas?: XcsCanvasPanel[];
    device?: XcsDevice;
}

// ============================================================
// Device Workarea Mapping
// ============================================================

interface DeviceWorkarea {
    width: number;   // mm
    height: number;  // mm
    name: string;    // Human-readable name
}

/**
 * Map of xTool device IDs to their workarea dimensions
 * These are approximate values - actual may vary slightly
 */
const XTOOL_DEVICE_WORKAREAS: Record<string, DeviceWorkarea> = {
    'F1': { width: 115, height: 115, name: 'xTool F1' },
    'F1_ULTRA': { width: 115, height: 115, name: 'xTool F1 Ultra' },
    'P2': { width: 285, height: 425, name: 'xTool P2' },
    'D1': { width: 432, height: 406, name: 'xTool D1' },
    'D1_PRO': { width: 430, height: 400, name: 'xTool D1 Pro' },
    'M1': { width: 307, height: 216, name: 'xTool M1' },
    'S1': { width: 498, height: 319, name: 'xTool S1' },
};

// ============================================================
// Extended ImportResult with process settings
// ============================================================

export interface XcsDeviceInfo {
    deviceId?: string;       // Device ID from XCS file (e.g., "F1")
    deviceName?: string;     // Human-readable name (e.g., "xTool F1")
    workAreaWidth?: number;  // XCS file's expected workarea width in mm
    workAreaHeight?: number; // XCS file's expected workarea height in mm
}

export interface XcsImportResult extends ImportResult {
    processSettings?: Map<number, ProcessSettingData[]>;
    warnings: string[];
    xcsDeviceInfo?: XcsDeviceInfo;  // Device info from XCS file for workarea mismatch warning
}

export interface ProcessSettingData {
    operation: 'score' | 'engrave' | 'cut';
    // Universal fields
    power?: number;
    speed?: number;
    passes?: number;
    lightSource?: string;
    outputEnabled?: boolean;  // From processIgnore (inverted)
    // Kerf compensation (score + cut)
    kerfEnabled?: boolean;
    kerfDistance?: number;
    // Engrave specific (FILL_VECTOR_ENGRAVING)
    density?: number;
    scanMode?: string;
    bitmapEngraveMode?: string;  // 'normal' | 'reverse'
    dotDuration?: number;        // microseconds
    // Cut specific - breakpoints/tabs (VECTOR_CUTTING)
    enableBreakPoint?: boolean;
    breakPointSize?: number;
    breakPointCount?: number;
    breakPointMode?: string;     // 'count' | 'distance'
    breakPointDistance?: number;
    breakPointPower?: number;
}

// ============================================================
// Operation Mapping (using processingType from device data)
// ============================================================

/**
 * Map XCS processingType to score/engrave/cut flags
 * This is the PRIMARY method - uses the actual operation type from device data
 *
 * NOTE: processIgnore does NOT affect which operation is enabled - it only affects
 * outputEnabled (handled separately in extractProcessSettings). The shape should
 * still render with its correct operation color even when processIgnore=true.
 */
function mapProcessingTypeToOperations(
    processingType: string | undefined,
    _processIgnore: boolean, // Kept for API compatibility, but not used here
    layerColor: string | undefined
): {
    score: boolean;
    engrave: boolean;
    cut: boolean;
    scoreColor: string;
    engraveColor: string;
    cutColor: string;
} {
    // Get color for the operation (use layerColor or default)
    const color = layerColor || XCS_COLOR_BLACK;

    // Map processingType to operations
    // processIgnore is handled via outputEnabled in process settings, not here
    switch (processingType) {
        case 'VECTOR_ENGRAVING':
            // Vector engraving = score (outline/stroke)
            return {
                score: true,
                engrave: false,
                cut: false,
                scoreColor: color,
                engraveColor: XCS_COLOR_BLACK,
                cutColor: XCS_COLOR_CYAN,
            };
        case 'FILL_VECTOR_ENGRAVING':
            // Fill vector engraving = engrave (filled/raster)
            return {
                score: false,
                engrave: true,
                cut: false,
                scoreColor: XCS_COLOR_RED,
                engraveColor: color,
                cutColor: XCS_COLOR_CYAN,
            };
        case 'VECTOR_CUTTING':
            // Vector cutting = cut (through material)
            return {
                score: false,
                engrave: false,
                cut: true,
                scoreColor: XCS_COLOR_RED,
                engraveColor: XCS_COLOR_BLACK,
                cutColor: color,
            };
        default:
            // Default to engrave if no processingType specified
            return {
                score: false,
                engrave: true,
                cut: false,
                scoreColor: XCS_COLOR_RED,
                engraveColor: color,
                cutColor: XCS_COLOR_CYAN,
            };
    }
}

// ============================================================
// SVG Styling Helper
// ============================================================

/**
 * Generate SVG path attributes based on XCS shape's fill and stroke properties
 * isFill=true means filled (engrave), isFill=false means stroked (score/cut)
 */
function getSvgPathStyle(shape: XcsShape, _shapeWidth: number, _shapeHeight: number): string {
    const strokeVisible = shape.stroke?.visible ?? true;
    const fillVisible = shape.fill?.visible ?? false;
    const isFill = shape.isFill ?? fillVisible;

    // XCS stroke width is in UI pixels, not mm. For our SVG, use a very thin stroke
    // to match xTool Studio's hairline appearance. 0.05% of shape size works well.
    const shapeSize = Math.max(_shapeWidth, _shapeHeight);
    const strokeWidth = Math.max(0.1, shapeSize * 0.0005); // 0.05% of shape size, min 0.1mm

    if (isFill && fillVisible) {
        // Filled shape (engrave)
        return 'fill="currentColor" stroke="none"';
    } else if (strokeVisible) {
        // Stroked shape (score/cut) - use miter join to match xTool's sharp corners
        return `fill="none" stroke="currentColor" stroke-width="${strokeWidth.toFixed(2)}" stroke-linecap="butt" stroke-linejoin="miter"`;
    } else {
        // Default to stroke
        return `fill="none" stroke="currentColor" stroke-width="${strokeWidth.toFixed(2)}"`;
    }
}

// ============================================================
// Device Data Parsing
// ============================================================

/**
 * Parse XCS Map-like structure to get display settings
 */
function parseDeviceDisplays(device: XcsFile['device']): Map<string, XcsDeviceDisplay> {
    const displayMap = new Map<string, XcsDeviceDisplay>();

    if (!device?.data?.value) {
        console.log('[parseDeviceDisplays] No device.data.value');
        return displayMap;
    }

    // device.data is a Map with [canvasId, canvasData] entries
    for (const [_canvasId, canvasData] of device.data.value) {
        if (!canvasData?.displays?.value) {
            console.log('[parseDeviceDisplays] No canvasData.displays.value for canvas', _canvasId);
            continue;
        }

        // displays is a Map with [shapeId, displaySettings] entries
        console.log('[parseDeviceDisplays] Processing canvas', _canvasId, 'with', canvasData.displays.value.length, 'display entries');
        for (const [shapeId, displaySettings] of canvasData.displays.value) {
            console.log('[parseDeviceDisplays] Adding shapeId:', shapeId, 'processingType:', displaySettings?.processingType, 'processIgnore:', displaySettings?.processIgnore);
            displayMap.set(shapeId, displaySettings);
        }
    }

    console.log('[parseDeviceDisplays] Final map has', displayMap.size, 'entries');
    return displayMap;
}

// ============================================================
// Shape Parsing Functions
// ============================================================

/**
 * Parse XCS RECT shape into TemplateShape
 * XCS x,y is TOP-LEFT (same as our canvas)
 *
 * @param offsetX - X offset to apply (shifts content so bounding box starts at 0)
 * @param offsetY - Y offset to apply (shifts content so bounding box starts at 0)
 */
function parseRectShape(
    shape: XcsShape,
    orderIndex: number,
    displaySettings: XcsDeviceDisplay | undefined,
    offsetX: number = 0,
    offsetY: number = 0
): Omit<TemplateShape, 'id' | 'templateId'> | null {
    const scaleX = shape.scale?.x ?? 1;
    const scaleY = shape.scale?.y ?? 1;
    const width = shape.width * scaleX;
    const height = shape.height * scaleY;

    // XCS x,y is TOP-LEFT - use directly with offset
    const x = shape.x + offsetX;
    const y = shape.y + offsetY;
    const rotation = shape.angle ?? 0;

    // For RECT shapes: These are field placeholders in our template system.
    // Even if processIgnore=true in xTool (meaning the rect outline shouldn't be processed),
    // the TEXT that gets placed in this field SHOULD be processed.
    // So we ignore processIgnore for rects and default to engrave=true.
    const processingType = displaySettings?.processingType;
    const ops = mapProcessingTypeToOperations(processingType, false, shape.layerColor || shape.layerTag);

    // If no processingType was set and it was marked as ignored, ensure engrave is enabled
    const finalOps = (!processingType && displaySettings?.processIgnore)
        ? { ...ops, engrave: true }
        : ops;

    return {
        type: 'rect',
        x: parseFloat(x.toFixed(2)),
        y: parseFloat(y.toFixed(2)),
        width: parseFloat(width.toFixed(2)),
        height: parseFloat(height.toFixed(2)),
        rotation,
        orderIndex,
        fieldId: undefined,
        ...finalOps,
        // Preserve original XCS data for lossless round-trip export
        originalXcsData: {
            type: 'RECT',
            layerTag: shape.layerTag,
            layerColor: shape.layerColor,
            isFill: shape.isFill,
            originalZOrder: shape.zOrder,
        },
    };
}

/**
 * Parse XCS PATH shape into TemplateShape
 * XCS x,y is TOP-LEFT (same as our canvas)
 *
 * IMPORTANT: The dPath may be in ABSOLUTE canvas coordinates, not local (0,0) coordinates.
 * The graphicX/graphicY properties indicate the path's origin offset.
 * If they're 0 but shape.x/y are non-zero, the path uses canvas coordinates.
 * We use the viewBox to map from path coordinates to rendered size.
 *
 * @param offsetX - X offset to apply (shifts content so bounding box starts at 0)
 * @param offsetY - Y offset to apply (shifts content so bounding box starts at 0)
 */
function parsePathShape(
    shape: XcsShape,
    orderIndex: number,
    displaySettings: XcsDeviceDisplay | undefined,
    offsetX: number = 0,
    offsetY: number = 0
): Omit<TemplateShape, 'id' | 'templateId'> | null {
    if (!shape.dPath) return null;

    // For PATH shapes, width/height are already the final mm dimensions
    // The scale factor is used for mapping dPath coordinates, not for final size
    // (Unlike other shape types where dimensions may be in canvas units)
    const width = shape.width;
    const height = shape.height;

    // XCS x,y is TOP-LEFT - use directly with offset
    const x = shape.x + offsetX;
    const y = shape.y + offsetY;
    const rotation = shape.angle ?? 0;

    // CRITICAL: dPath coordinates are in ABSOLUTE canvas space, not relative to shape position!
    // For complex paths (like monograms), path coords can be at (773, 514) while shape.x/y is (8, 4)
    // We must calculate the actual path bounds and use those for the viewBox
    const pathBounds = calculatePathBounds(shape.dPath);
    const viewBoxX = pathBounds.minX;
    const viewBoxY = pathBounds.minY;
    const viewBoxW = pathBounds.maxX - pathBounds.minX;
    const viewBoxH = pathBounds.maxY - pathBounds.minY;

    const fillRule = shape.fillRule || 'nonzero';
    const pathStyle = getSvgPathStyle(shape, width, height);

    // viewBox maps from actual path coordinate space to rendered size
    // Use mm units so browser renders at correct physical size (not tiny pixel size)
    const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxX.toFixed(2)} ${viewBoxY.toFixed(2)} ${viewBoxW.toFixed(2)} ${viewBoxH.toFixed(2)}" width="${width.toFixed(2)}mm" height="${height.toFixed(2)}mm">
  <path d="${shape.dPath}" fill-rule="${fillRule}" ${pathStyle}/>
</svg>`;

    const processIgnore = displaySettings?.processIgnore ?? false;
    const processingType = displaySettings?.processingType;
    const ops = mapProcessingTypeToOperations(processingType, processIgnore, shape.layerColor || shape.layerTag);

    return {
        type: 'graphic',
        x: parseFloat(x.toFixed(2)),
        y: parseFloat(y.toFixed(2)),
        width: parseFloat(width.toFixed(2)),
        height: parseFloat(height.toFixed(2)),
        rotation,
        orderIndex,
        fieldId: undefined,
        svgData: svgContent,
        ...ops,
        // Preserve original XCS data for lossless round-trip export
        originalXcsData: {
            type: 'PATH',
            dPath: shape.dPath,
            fillRule: fillRule,
            layerTag: shape.layerTag,
            layerColor: shape.layerColor,
            isFill: shape.isFill,
            originalZOrder: shape.zOrder,
        },
    };
}

/**
 * Parse XCS TEXT shape into TemplateShape
 * Converts glyphs to SVG path with proper positioning
 *
 * IMPORTANT: For TEXT shapes in XCS:
 * - shape.width/height = FINAL rendered dimensions (already the target size)
 * - shape.scale = glyph scale factor (fontSize / 72) - only used for glyph positioning
 * - Glyphs are at size 72 in the glyphData, scale tells us the font size
 * - XCS x,y is TOP-LEFT (same as our canvas)
 *
 * The width/height are NOT multiplied by scale - they're already the final size!
 *
 * @param offsetX - X offset to apply (shifts content so bounding box starts at 0)
 * @param offsetY - Y offset to apply (shifts content so bounding box starts at 0)
 */
function parseTextShape(
    shape: XcsShape,
    orderIndex: number,
    displaySettings: XcsDeviceDisplay | undefined,
    offsetX: number = 0,
    offsetY: number = 0
): Omit<TemplateShape, 'id' | 'templateId'> | null {
    const glyphData = shape.fontData?.glyphData;
    const text = shape.text || '';

    if (!text) return null;

    // For TEXT shapes, width/height ARE the final rendered dimensions
    // DO NOT multiply by scale - that's already factored into these values
    const width = shape.width;
    const height = shape.height;

    // XCS x,y is TOP-LEFT - use directly with offset
    const x = shape.x + offsetX;
    const y = shape.y + offsetY;
    const rotation = shape.angle ?? 0;

    // If we have glyph data, build a proper SVG path from glyphs
    if (glyphData && Object.keys(glyphData).length > 0) {
        // Get font metrics - ascent is used for glyph Y positioning
        const fontInfo = shape.fontData?.fontInfo;
        const ascent = fontInfo?.ascent ?? 60;

        // Calculate total text width (used for glyph X positioning)
        let totalAdvance = 0;
        for (const char of text) {
            const glyph = glyphData[char];
            if (glyph?.advanceWidth) {
                totalAdvance += glyph.advanceWidth;
            }
        }

        // Build path for each character, positioned correctly
        const paths: string[] = [];
        let currentX = 0;

        for (const char of text) {
            const glyph = glyphData[char];
            if (glyph?.dPath) {
                // Transform glyph path to position it correctly
                // Glyph Y coordinates are negative (font convention: Y goes up)
                const transformedPath = translatePath(glyph.dPath, currentX, ascent);
                paths.push(transformedPath);
            }
            if (glyph?.advanceWidth) {
                currentX += glyph.advanceWidth;
            }
        }

        if (paths.length > 0) {
            const combinedPath = paths.join(' ');

            // Calculate ACTUAL bounding box of the rendered glyphs
            // This is more accurate than using theoretical font metrics (totalAdvance x lineHeight)
            const bounds = calculatePathBounds(combinedPath);
            const actualWidth = bounds.maxX - bounds.minX;
            const actualHeight = bounds.maxY - bounds.minY;

            // Scale from actual glyph bounds to shape dimensions
            const scaleX = actualWidth > 0 ? width / actualWidth : 1;
            const scaleY = actualHeight > 0 ? height / actualHeight : 1;

            // Translate to move glyph origin to (0,0), then scale to fit shape
            // The transform order is: first translate (to origin), then scale
            const translateX = -bounds.minX;
            const translateY = -bounds.minY;

            // Pre-compensate stroke-width for the scale transform
            // Text needs thin strokes (0.15mm target) like other path shapes
            const avgScale = (scaleX + scaleY) / 2;
            const targetStrokeWidth = 0.15; // Match original thin stroke appearance
            const compensatedStrokeWidth = targetStrokeWidth / avgScale;

            // Use shape dimensions for viewBox (matching SVG dimensions = no browser scaling)
            // Apply transform: translate to origin, then scale to fit shape bounds
            // Use mm units so browser renders at correct physical size
            const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}mm" height="${height}mm">
  <g transform="scale(${scaleX.toFixed(6)}, ${scaleY.toFixed(6)}) translate(${translateX.toFixed(6)}, ${translateY.toFixed(6)})">
    <path d="${combinedPath}" fill-rule="evenodd" fill="currentColor" stroke="currentColor" stroke-width="${compensatedStrokeWidth.toFixed(2)}"/>
  </g>
</svg>`;

            // For TEXT shapes: These contain actual content to be engraved.
            // Even if processIgnore=true in xTool, we want the text to render.
            // So we ignore processIgnore and default to engrave=true.
            const processingType = displaySettings?.processingType;
            const ops = mapProcessingTypeToOperations(processingType, false, shape.layerColor || shape.layerTag);
            const finalOps = (!processingType && displaySettings?.processIgnore)
                ? { ...ops, engrave: true }
                : ops;

            // Calculate fontSize from scale (glyph data is at 72 units reference)
            const fontSize = height / (actualHeight / 72);

            return {
                type: 'graphic',
                x: parseFloat(x.toFixed(2)),
                y: parseFloat(y.toFixed(2)),
                width: parseFloat(width.toFixed(2)),
                height: parseFloat(height.toFixed(2)),
                rotation,
                orderIndex,
                fieldId: undefined,
                svgData: svgContent,
                ...finalOps,
                // Preserve original XCS data for lossless round-trip export
                originalXcsData: {
                    type: 'TEXT',
                    text: text,
                    fontFamily: shape.style?.fontFamily || 'Arial',
                    fontSize: fontSize,
                    fontData: shape.fontData ? {
                        fontInfo: shape.fontData.fontInfo,
                        glyphData: shape.fontData.glyphData as Record<string, OriginalXcsGlyphData>,
                    } : undefined,
                    // Preserve exact positioning for lossless round-trip
                    originalX: shape.x,        // XCS x (already centered for TEXT)
                    originalY: shape.y,        // XCS y
                    originalWidth: shape.width,
                    originalHeight: shape.height,
                    originalScale: shape.scale,  // { x: number, y: number }
                    layerTag: shape.layerTag,
                    layerColor: shape.layerColor,
                    isFill: shape.isFill,
                    originalZOrder: shape.zOrder,
                },
            };
        }
    }

    // No glyph data - create a placeholder rect for the text bounds
    // Same logic: ignore processIgnore, default to engrave=true
    const processingType = displaySettings?.processingType;
    const ops = mapProcessingTypeToOperations(processingType, false, shape.layerColor || shape.layerTag);
    const finalOps = (!processingType && displaySettings?.processIgnore)
        ? { ...ops, engrave: true }
        : ops;

    return {
        type: 'rect',
        x: parseFloat(x.toFixed(2)),
        y: parseFloat(y.toFixed(2)),
        width: parseFloat(width.toFixed(2)),
        height: parseFloat(height.toFixed(2)),
        rotation,
        orderIndex,
        fieldId: undefined,
        ...finalOps,
    };
}

/**
 * Parse XCS CIRCLE shape into TemplateShape
 * In xTool format: x,y is TOP-LEFT, uses width/height (not radius)
 * width/height can be different (ellipse-like)
 *
 * NOTE: Sometimes CIRCLE shapes have very small scale values (e.g., 0.01)
 * which would make the shape tiny. If the unscaled dimensions are reasonable
 * (> 1mm) but scaled would be < 1mm, we don't apply the scale.
 */
function parseCircleShape(
    shape: XcsShape,
    orderIndex: number,
    displaySettings: XcsDeviceDisplay | undefined,
    offsetX: number = 0,
    offsetY: number = 0
): Omit<TemplateShape, 'id' | 'templateId'> | null {
    const scaleX = shape.scale?.x ?? 1;
    const scaleY = shape.scale?.y ?? 1;

    // Check if scale would make the shape too small
    // If unscaled is > 1mm but scaled would be < 1mm, don't apply scale
    let width = shape.width * scaleX;
    let height = shape.height * scaleY;

    if (shape.width > 1 && width < 1) {
        width = shape.width;
    }
    if (shape.height > 1 && height < 1) {
        height = shape.height;
    }

    if (width <= 0 || height <= 0) return null;

    // x,y is TOP-LEFT - use directly with offset
    const x = shape.x + offsetX;
    const y = shape.y + offsetY;
    const rotation = shape.angle ?? 0;

    // Generate SVG path - radii are half of width/height
    const rx = width / 2;
    const ry = height / 2;
    const pathD = rx === ry
        ? circleToPath(rx, ry, rx)
        : ellipseToPath(rx, ry, rx, ry);
    const pathStyle = getSvgPathStyle(shape, width, height);

    // Use mm units so browser renders at correct physical size (not tiny pixel size)
    const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width.toFixed(2)} ${height.toFixed(2)}" width="${width.toFixed(2)}mm" height="${height.toFixed(2)}mm">
  <path d="${pathD}" ${pathStyle}/>
</svg>`;

    const processIgnore = displaySettings?.processIgnore ?? false;
    const processingType = displaySettings?.processingType;
    const ops = mapProcessingTypeToOperations(processingType, processIgnore, shape.layerColor || shape.layerTag);

    return {
        type: 'graphic',
        x: parseFloat(x.toFixed(2)),
        y: parseFloat(y.toFixed(2)),
        width: parseFloat(width.toFixed(2)),
        height: parseFloat(height.toFixed(2)),
        rotation,
        orderIndex,
        fieldId: undefined,
        svgData: svgContent,
        ...ops,
        // Preserve original XCS data for lossless round-trip export
        originalXcsData: {
            type: 'CIRCLE',
            layerTag: shape.layerTag,
            layerColor: shape.layerColor,
            isFill: shape.isFill,
            originalZOrder: shape.zOrder,
        },
    };
}

/**
 * Parse XCS ELLIPSE shape into TemplateShape
 * In xTool format: x,y is TOP-LEFT, uses width/height
 */
function parseEllipseShape(
    shape: XcsShape,
    orderIndex: number,
    displaySettings: XcsDeviceDisplay | undefined,
    offsetX: number = 0,
    offsetY: number = 0
): Omit<TemplateShape, 'id' | 'templateId'> | null {
    const scaleX = shape.scale?.x ?? 1;
    const scaleY = shape.scale?.y ?? 1;

    // xTool uses width/height, x,y is TOP-LEFT
    const width = shape.width * scaleX;
    const height = shape.height * scaleY;

    if (width <= 0 || height <= 0) return null;

    // x,y is TOP-LEFT - use directly with offset
    const x = shape.x + offsetX;
    const y = shape.y + offsetY;
    const rotation = shape.angle ?? 0;

    // Generate SVG path - radii are half of width/height
    const rx = width / 2;
    const ry = height / 2;
    const pathD = ellipseToPath(rx, ry, rx, ry);
    const pathStyle = getSvgPathStyle(shape, width, height);

    // Use mm units so browser renders at correct physical size (not tiny pixel size)
    const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width.toFixed(2)} ${height.toFixed(2)}" width="${width.toFixed(2)}mm" height="${height.toFixed(2)}mm">
  <path d="${pathD}" ${pathStyle}/>
</svg>`;

    const processIgnore = displaySettings?.processIgnore ?? false;
    const processingType = displaySettings?.processingType;
    const ops = mapProcessingTypeToOperations(processingType, processIgnore, shape.layerColor || shape.layerTag);

    return {
        type: 'graphic',
        x: parseFloat(x.toFixed(2)),
        y: parseFloat(y.toFixed(2)),
        width: parseFloat(width.toFixed(2)),
        height: parseFloat(height.toFixed(2)),
        rotation,
        orderIndex,
        fieldId: undefined,
        svgData: svgContent,
        ...ops,
        // Preserve original XCS data for lossless round-trip export
        originalXcsData: {
            type: 'ELLIPSE',
            layerTag: shape.layerTag,
            layerColor: shape.layerColor,
            isFill: shape.isFill,
            originalZOrder: shape.zOrder,
        },
    };
}

/**
 * Parse XCS LINE shape into TemplateShape
 * LINE is a polyline defined by an array of points
 */
function parseLineShape(
    shape: XcsShape,
    orderIndex: number,
    displaySettings: XcsDeviceDisplay | undefined,
    offsetX: number = 0,
    offsetY: number = 0
): Omit<TemplateShape, 'id' | 'templateId'> | null {
    const points = shape.points;
    if (!points || points.length < 2) return null;

    const scaleX = shape.scale?.x ?? 1;
    const scaleY = shape.scale?.y ?? 1;

    // Scale and calculate bounding box
    const scaledPoints = points.map(p => ({
        x: p.x * scaleX,
        y: p.y * scaleY
    }));

    const xs = scaledPoints.map(p => p.x);
    const ys = scaledPoints.map(p => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);

    const width = maxX - minX || 1; // Prevent zero width
    const height = maxY - minY || 1; // Prevent zero height

    // Normalize points to local coordinates (0,0 based)
    const localPoints = scaledPoints.map(p => ({
        x: p.x - minX,
        y: p.y - minY
    }));

    // Position is shape position plus local min, with offset
    const x = shape.x + minX + offsetX;
    const y = shape.y + minY + offsetY;
    const rotation = shape.angle ?? 0;

    const pathD = lineToPath(localPoints);
    const pathStyle = getSvgPathStyle(shape, width, height);

    // Use mm units so browser renders at correct physical size (not tiny pixel size)
    const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width.toFixed(2)} ${height.toFixed(2)}" width="${width.toFixed(2)}mm" height="${height.toFixed(2)}mm">
  <path d="${pathD}" ${pathStyle}/>
</svg>`;

    const processIgnore = displaySettings?.processIgnore ?? false;
    const processingType = displaySettings?.processingType;
    // Lines default to score (stroke) operation
    const ops = mapProcessingTypeToOperations(processingType || 'VECTOR_ENGRAVING', processIgnore, shape.layerColor || shape.layerTag);

    return {
        type: 'graphic',
        x: parseFloat(x.toFixed(2)),
        y: parseFloat(y.toFixed(2)),
        width: parseFloat(width.toFixed(2)),
        height: parseFloat(height.toFixed(2)),
        rotation,
        orderIndex,
        fieldId: undefined,
        svgData: svgContent,
        ...ops,
        // Preserve original XCS data for lossless round-trip export
        originalXcsData: {
            type: 'LINE',
            layerTag: shape.layerTag,
            layerColor: shape.layerColor,
            isFill: shape.isFill,
            originalZOrder: shape.zOrder,
        },
    };
}

/**
 * Parse XCS STAR shape into TemplateShape
 * In xTool format: x,y is TOP-LEFT, uses width/height/count/starInnerScale
 *
 * Uses the same scale+translate approach as parsePolygonShape:
 * 1. Generate reference star at known center
 * 2. Calculate actual bounding box
 * 3. Use transform to scale and translate to fit target dimensions
 *
 * This ensures the star fits exactly within its bounding box, with the top
 * point at y=0 (not clipped above the viewBox).
 */
function parseStarShape(
    shape: XcsShape,
    orderIndex: number,
    displaySettings: XcsDeviceDisplay | undefined,
    offsetX: number = 0,
    offsetY: number = 0
): Omit<TemplateShape, 'id' | 'templateId'> | null {
    const scaleX = shape.scale?.x ?? 1;
    const scaleY = shape.scale?.y ?? 1;

    // xTool uses width/height, x,y is TOP-LEFT
    const width = shape.width * scaleX;
    const height = shape.height * scaleY;

    if (width <= 0 || height <= 0) return null;

    // x,y is TOP-LEFT - use directly with offset
    const x = shape.x + offsetX;
    const y = shape.y + offsetY;
    const rotation = shape.angle ?? 0;

    // xTool uses 'count' for number of points, 'starInnerScale' for inner/outer ratio
    const numPoints = shape.count ?? shape.numPoints ?? 5;
    const innerScale = shape.starInnerScale ?? 0.4; // Default ~0.382 for golden ratio star

    // Generate star at a reference size first, then scale to fit bounding box
    // (same approach as parsePolygonShape for consistent handling)
    const refRadius = 100;
    const refCx = refRadius;
    const refCy = refRadius;
    const pathD = starToPath(refCx, refCy, refRadius, refRadius, refRadius * innerScale, refRadius * innerScale, numPoints);

    // Calculate actual bounds of the generated star
    const bounds = calculatePathBounds(pathD);
    const actualWidth = bounds.maxX - bounds.minX;
    const actualHeight = bounds.maxY - bounds.minY;

    // Scale to fit target dimensions
    const scaleXFactor = actualWidth > 0 ? width / actualWidth : 1;
    const scaleYFactor = actualHeight > 0 ? height / actualHeight : 1;

    // Translate to move star origin to (0,0), then scale to fit
    const translateX = -bounds.minX;
    const translateY = -bounds.minY;

    // Pre-compensate stroke-width for the scale transform
    // Target 0.15mm to match XCS thin stroke appearance (same as other shapes)
    const avgScale = (scaleXFactor + scaleYFactor) / 2;
    const targetStrokeWidth = 0.15;
    const compensatedStrokeWidth = targetStrokeWidth / avgScale;

    let pathStyle = getSvgPathStyle(shape, width, height);
    // Override stroke-width with compensated value (so after scale transform it becomes ~0.15mm)
    pathStyle = pathStyle.replace(/stroke-width="[^"]*"/, `stroke-width="${compensatedStrokeWidth.toFixed(2)}"`);

    // Use mm units so browser renders at correct physical size (not tiny pixel size)
    // Apply transform: translate to origin, then scale to fit bounding box
    const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width.toFixed(2)} ${height.toFixed(2)}" width="${width.toFixed(2)}mm" height="${height.toFixed(2)}mm">
  <g transform="scale(${scaleXFactor.toFixed(6)}, ${scaleYFactor.toFixed(6)}) translate(${translateX.toFixed(6)}, ${translateY.toFixed(6)})">
    <path d="${pathD}" ${pathStyle}/>
  </g>
</svg>`;

    const processIgnore = displaySettings?.processIgnore ?? false;
    const processingType = displaySettings?.processingType;
    console.log('[STAR] shape.id:', shape.id, 'displaySettings:', displaySettings ? { processingType, processIgnore } : 'UNDEFINED');
    const ops = mapProcessingTypeToOperations(processingType, processIgnore, shape.layerColor || shape.layerTag);
    console.log('[STAR] ops:', ops);

    return {
        type: 'graphic',
        x: parseFloat(x.toFixed(2)),
        y: parseFloat(y.toFixed(2)),
        width: parseFloat(width.toFixed(2)),
        height: parseFloat(height.toFixed(2)),
        rotation,
        orderIndex,
        fieldId: undefined,
        svgData: svgContent,
        ...ops,
        // Preserve original XCS data for lossless round-trip export
        originalXcsData: {
            type: 'STAR',
            count: numPoints,
            starInnerScale: innerScale,
            layerTag: shape.layerTag,
            layerColor: shape.layerColor,
            isFill: shape.isFill,
            originalZOrder: shape.zOrder,
        },
    };
}

/**
 * Parse XCS REGULAR_POLYGON shape into TemplateShape
 * In xTool format: x,y is TOP-LEFT, uses width/height/count
 */
function parsePolygonShape(
    shape: XcsShape,
    orderIndex: number,
    displaySettings: XcsDeviceDisplay | undefined,
    offsetX: number = 0,
    offsetY: number = 0
): Omit<TemplateShape, 'id' | 'templateId'> | null {
    const scaleX = shape.scale?.x ?? 1;
    const scaleY = shape.scale?.y ?? 1;

    // xTool uses width/height, x,y is TOP-LEFT
    const width = shape.width * scaleX;
    const height = shape.height * scaleY;

    if (width <= 0 || height <= 0) return null;

    // xTool uses 'count' for number of sides
    const sides = shape.count ?? shape.sides ?? 6; // Default hexagon
    if (sides < 3) return null;

    // x,y is TOP-LEFT - use directly with offset
    const x = shape.x + offsetX;
    const y = shape.y + offsetY;
    const rotation = shape.angle ?? 0;

    // Generate polygon at a reference size first, then scale to fit bounding box
    // A polygon with radius R doesn't have width 2R - it depends on vertex angles
    // So we generate at unit size, measure actual bounds, and scale to fit
    const refRadius = 100; // Reference radius for generation
    const refCx = refRadius;
    const refCy = refRadius;
    const pathD = polygonToPath(refCx, refCy, refRadius, refRadius, sides);

    // Calculate actual bounds of the generated polygon
    const bounds = calculatePathBounds(pathD);
    const actualWidth = bounds.maxX - bounds.minX;
    const actualHeight = bounds.maxY - bounds.minY;

    // Scale to fit target dimensions
    const scaleXFactor = actualWidth > 0 ? width / actualWidth : 1;
    const scaleYFactor = actualHeight > 0 ? height / actualHeight : 1;

    // Translate to move polygon origin to (0,0), then scale to fit
    const translateX = -bounds.minX;
    const translateY = -bounds.minY;

    // Pre-compensate stroke-width for the scale transform
    // Target 0.15mm to match XCS thin stroke appearance (same as star and text)
    const avgScale = (scaleXFactor + scaleYFactor) / 2;
    const targetStrokeWidth = 0.15;
    const compensatedStrokeWidth = targetStrokeWidth / avgScale;

    let pathStyle = getSvgPathStyle(shape, width, height);
    // Override stroke-width with compensated value (so after scale transform it becomes ~0.15mm)
    pathStyle = pathStyle.replace(/stroke-width="[^"]*"/, `stroke-width="${compensatedStrokeWidth.toFixed(2)}"`);

    // Use mm units so browser renders at correct physical size (not tiny pixel size)
    // Apply transform: translate to origin, then scale to fit bounding box
    const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width.toFixed(2)} ${height.toFixed(2)}" width="${width.toFixed(2)}mm" height="${height.toFixed(2)}mm">
  <g transform="scale(${scaleXFactor.toFixed(6)}, ${scaleYFactor.toFixed(6)}) translate(${translateX.toFixed(6)}, ${translateY.toFixed(6)})">
    <path d="${pathD}" ${pathStyle}/>
  </g>
</svg>`;

    const processIgnore = displaySettings?.processIgnore ?? false;
    const processingType = displaySettings?.processingType;
    console.log('[POLYGON] shape.id:', shape.id, 'displaySettings:', displaySettings ? { processingType, processIgnore } : 'UNDEFINED');
    const ops = mapProcessingTypeToOperations(processingType, processIgnore, shape.layerColor || shape.layerTag);
    console.log('[POLYGON] ops:', ops);

    return {
        type: 'graphic',
        x: parseFloat(x.toFixed(2)),
        y: parseFloat(y.toFixed(2)),
        width: parseFloat(width.toFixed(2)),
        height: parseFloat(height.toFixed(2)),
        rotation,
        orderIndex,
        fieldId: undefined,
        svgData: svgContent,
        ...ops,
        // Preserve original XCS data for lossless round-trip export
        originalXcsData: {
            type: 'POLYGON',
            sides: sides,
            layerTag: shape.layerTag,
            layerColor: shape.layerColor,
            isFill: shape.isFill,
            originalZOrder: shape.zOrder,
        },
    };
}

/**
 * Parse XCS BITMAP shape into TemplateShape (raster image)
 *
 * XCS BITMAP stores:
 * - x, y: position (top-left corner)
 * - width, height: FINAL display dimensions (already scaled from originWidth/Height)
 * - scale.x, scale.y: conversion factors from origin pixels (NOT to be applied again)
 * - originWidth, originHeight: original image pixel dimensions
 *
 * @param offsetX - X offset to apply (shifts content so bounding box starts at 0)
 * @param offsetY - Y offset to apply (shifts content so bounding box starts at 0)
 */
function parseBitmapShape(
    shape: XcsShape,
    orderIndex: number,
    _displaySettings: XcsDeviceDisplay | undefined,
    offsetX: number = 0,
    offsetY: number = 0
): Omit<TemplateShape, 'id' | 'templateId'> | null {
    // BITMAP uses base64 data URI
    if (!shape.base64) return null;

    // XCS BITMAP width/height are ALREADY the final display dimensions
    // The scale factors represent conversion from origin pixels and should NOT be applied
    const width = shape.width;
    const height = shape.height;

    // Position uses x/y (not left/top - that was incorrect assumption)
    const x = (shape.x ?? shape.left ?? 0) + offsetX;
    const y = (shape.y ?? shape.top ?? 0) + offsetY;
    const rotation = shape.angle ?? 0;

    // Detect format from base64 data URI
    let rasterFormat: 'png' | 'jpg' | 'bmp' = 'png';
    if (shape.base64.startsWith('data:image/jpeg') || shape.base64.startsWith('data:image/jpg')) {
        rasterFormat = 'jpg';
    } else if (shape.base64.startsWith('data:image/bmp')) {
        rasterFormat = 'bmp';
    }

    // Rasters default to engrave-only (BITMAP_ENGRAVING in XCS)
    return {
        type: 'raster',
        x: parseFloat(x.toFixed(2)),
        y: parseFloat(y.toFixed(2)),
        width: parseFloat(width.toFixed(2)),
        height: parseFloat(height.toFixed(2)),
        rotation,
        orderIndex,
        fieldId: undefined,
        // Raster-specific fields
        rasterData: shape.base64,  // Already has data:image/png;base64, prefix
        rasterFormat,
        // Store original PIXEL dimensions (originWidth/originHeight), not display size
        rasterOriginalWidth: shape.originWidth ?? Math.round(width),
        rasterOriginalHeight: shape.originHeight ?? Math.round(height),
        // Rasters only support engrave
        score: false,
        engrave: true,
        cut: false,
        engraveColor: '#000000',  // Default black for engrave
        // Preserve original XCS data for lossless round-trip export
        originalXcsData: {
            type: 'BITMAP',
            visible: shape.visible,
            lock: shape.lock,
            layerTag: shape.layerTag,
            layerColor: shape.layerColor,
            originalZOrder: shape.zOrder,
        },
    };
}

/**
 * Translate an SVG path by offsetting all coordinates
 * Handles M, L, C, Q, S, T, Z commands
 */
function translatePath(dPath: string, offsetX: number, offsetY: number): string {
    // Parse and transform path commands
    const commands: string[] = [];
    const regex = /([MLCSQTZ])([^MLCSQTZ]*)/gi;
    let match;

    while ((match = regex.exec(dPath)) !== null) {
        const cmd = match[1];
        const args = match[2].trim();
        const isRelative = cmd === cmd.toLowerCase();

        if (cmd.toUpperCase() === 'Z') {
            commands.push('Z');
            continue;
        }

        // Parse numbers from the arguments
        const numRegex = /-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g;
        const nums: number[] = [];
        let numMatch;
        while ((numMatch = numRegex.exec(args)) !== null) {
            nums.push(parseFloat(numMatch[0]));
        }

        // For absolute commands, offset the coordinates
        if (!isRelative) {
            switch (cmd.toUpperCase()) {
                case 'M':
                case 'L':
                case 'T':
                    // x, y pairs
                    for (let i = 0; i < nums.length; i += 2) {
                        nums[i] += offsetX;
                        nums[i + 1] += offsetY;
                    }
                    break;
                case 'C':
                    // x1, y1, x2, y2, x, y sextuplets
                    for (let i = 0; i < nums.length; i += 6) {
                        nums[i] += offsetX;
                        nums[i + 1] += offsetY;
                        nums[i + 2] += offsetX;
                        nums[i + 3] += offsetY;
                        nums[i + 4] += offsetX;
                        nums[i + 5] += offsetY;
                    }
                    break;
                case 'S':
                case 'Q':
                    // x1, y1, x, y quadruplets
                    for (let i = 0; i < nums.length; i += 4) {
                        nums[i] += offsetX;
                        nums[i + 1] += offsetY;
                        nums[i + 2] += offsetX;
                        nums[i + 3] += offsetY;
                    }
                    break;
            }
        }

        commands.push(cmd + nums.join(' '));
    }

    return commands.join('');
}

/**
 * Calculate the bounding box of an SVG path
 * Returns minX, minY, maxX, maxY of all coordinates in the path
 * Handles both absolute (uppercase) and relative (lowercase) commands
 */
function calculatePathBounds(dPath: string): { minX: number; minY: number; maxX: number; maxY: number } {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    // Track current position for relative commands
    let currentX = 0, currentY = 0;
    let startX = 0, startY = 0; // For Z command to return to start

    // Helper to update bounds
    const updateBounds = (x: number, y: number) => {
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
    };

    // Parse path commands - include all command letters (case-sensitive for relative vs absolute)
    const regex = /([MmLlHhVvCcSsQqTtAaZz])([^MmLlHhVvCcSsQqTtAaZz]*)/g;
    let match;

    while ((match = regex.exec(dPath)) !== null) {
        const cmd = match[1];
        const isRelative = cmd === cmd.toLowerCase();
        const cmdUpper = cmd.toUpperCase();

        if (cmdUpper === 'Z') {
            // Close path - return to start
            currentX = startX;
            currentY = startY;
            continue;
        }

        const args = match[2].trim();
        const numRegex = /-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g;
        const nums: number[] = [];
        let numMatch;
        while ((numMatch = numRegex.exec(args)) !== null) {
            nums.push(parseFloat(numMatch[0]));
        }

        // Process based on command type
        switch (cmdUpper) {
            case 'M':
                // MoveTo: x, y pairs (first pair sets subpath start)
                for (let i = 0; i < nums.length; i += 2) {
                    const x = isRelative ? currentX + nums[i] : nums[i];
                    const y = isRelative ? currentY + nums[i + 1] : nums[i + 1];
                    updateBounds(x, y);
                    currentX = x;
                    currentY = y;
                    if (i === 0) {
                        startX = x;
                        startY = y;
                    }
                }
                break;

            case 'L':
            case 'T':
                // LineTo or smooth quadratic: x, y pairs
                for (let i = 0; i < nums.length; i += 2) {
                    const x = isRelative ? currentX + nums[i] : nums[i];
                    const y = isRelative ? currentY + nums[i + 1] : nums[i + 1];
                    updateBounds(x, y);
                    currentX = x;
                    currentY = y;
                }
                break;

            case 'H':
                // Horizontal line: only x coordinates
                for (let i = 0; i < nums.length; i++) {
                    const x = isRelative ? currentX + nums[i] : nums[i];
                    updateBounds(x, currentY);
                    currentX = x;
                }
                break;

            case 'V':
                // Vertical line: only y coordinates
                for (let i = 0; i < nums.length; i++) {
                    const y = isRelative ? currentY + nums[i] : nums[i];
                    updateBounds(currentX, y);
                    currentY = y;
                }
                break;

            case 'C':
                // Cubic bezier: x1, y1, x2, y2, x, y sextuplets
                for (let i = 0; i < nums.length; i += 6) {
                    const baseX = isRelative ? currentX : 0;
                    const baseY = isRelative ? currentY : 0;
                    // Control point 1
                    updateBounds(baseX + nums[i], baseY + nums[i + 1]);
                    // Control point 2
                    updateBounds(baseX + nums[i + 2], baseY + nums[i + 3]);
                    // End point
                    const endX = baseX + nums[i + 4];
                    const endY = baseY + nums[i + 5];
                    updateBounds(endX, endY);
                    currentX = endX;
                    currentY = endY;
                }
                break;

            case 'S':
                // Smooth cubic bezier: x2, y2, x, y quadruplets
                for (let i = 0; i < nums.length; i += 4) {
                    const baseX = isRelative ? currentX : 0;
                    const baseY = isRelative ? currentY : 0;
                    // Control point 2
                    updateBounds(baseX + nums[i], baseY + nums[i + 1]);
                    // End point
                    const endX = baseX + nums[i + 2];
                    const endY = baseY + nums[i + 3];
                    updateBounds(endX, endY);
                    currentX = endX;
                    currentY = endY;
                }
                break;

            case 'Q':
                // Quadratic bezier: x1, y1, x, y quadruplets
                for (let i = 0; i < nums.length; i += 4) {
                    const baseX = isRelative ? currentX : 0;
                    const baseY = isRelative ? currentY : 0;
                    // Control point
                    updateBounds(baseX + nums[i], baseY + nums[i + 1]);
                    // End point
                    const endX = baseX + nums[i + 2];
                    const endY = baseY + nums[i + 3];
                    updateBounds(endX, endY);
                    currentX = endX;
                    currentY = endY;
                }
                break;

            case 'A':
                // Arc: rx, ry, rotation, large-arc, sweep, x, y (7 params per arc)
                // Only x,y endpoint affects bounds for our purposes
                for (let i = 0; i < nums.length; i += 7) {
                    const endX = isRelative ? currentX + nums[i + 5] : nums[i + 5];
                    const endY = isRelative ? currentY + nums[i + 6] : nums[i + 6];
                    updateBounds(endX, endY);
                    currentX = endX;
                    currentY = endY;
                }
                break;
        }
    }

    // Handle empty path
    if (minX === Infinity) {
        return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    }

    return { minX, minY, maxX, maxY };
}

// ============================================================
// Shape-to-Path Helper Functions
// ============================================================

/**
 * Convert a circle to an SVG path using bezier curves
 * Uses the standard bezier approximation with k = 0.552284749831
 */
function circleToPath(cx: number, cy: number, r: number): string {
    const k = r * 0.552284749831; // Magic number for bezier circle approximation
    return `M${cx},${cy - r} ` +
           `C${cx + k},${cy - r} ${cx + r},${cy - k} ${cx + r},${cy} ` +
           `C${cx + r},${cy + k} ${cx + k},${cy + r} ${cx},${cy + r} ` +
           `C${cx - k},${cy + r} ${cx - r},${cy + k} ${cx - r},${cy} ` +
           `C${cx - r},${cy - k} ${cx - k},${cy - r} ${cx},${cy - r} Z`;
}

/**
 * Convert an ellipse to an SVG path using bezier curves
 * Uses the standard bezier approximation with k = 0.552284749831
 */
function ellipseToPath(cx: number, cy: number, rx: number, ry: number): string {
    const kx = rx * 0.552284749831;
    const ky = ry * 0.552284749831;
    return `M${cx},${cy - ry} ` +
           `C${cx + kx},${cy - ry} ${cx + rx},${cy - ky} ${cx + rx},${cy} ` +
           `C${cx + rx},${cy + ky} ${cx + kx},${cy + ry} ${cx},${cy + ry} ` +
           `C${cx - kx},${cy + ry} ${cx - rx},${cy + ky} ${cx - rx},${cy} ` +
           `C${cx - rx},${cy - ky} ${cx - kx},${cy - ry} ${cx},${cy - ry} Z`;
}

/**
 * Convert a line/polyline (array of points) to an SVG path
 */
function lineToPath(points: { x: number; y: number }[]): string {
    if (points.length < 2) return '';
    return 'M' + points.map((p, i) => i === 0 ? `${p.x},${p.y}` : `L${p.x},${p.y}`).join(' ');
}

/**
 * Convert a star to an SVG path
 * Creates alternating outer and inner points
 * Uses separate X and Y radii to fill non-square bounding boxes
 */
function starToPath(cx: number, cy: number, outerRx: number, outerRy: number, innerRx: number, innerRy: number, numPoints: number): string {
    const path: string[] = [];
    const angleStep = Math.PI / numPoints;

    for (let i = 0; i < numPoints * 2; i++) {
        const angle = i * angleStep - Math.PI / 2; // Start from top
        const rx = i % 2 === 0 ? outerRx : innerRx;
        const ry = i % 2 === 0 ? outerRy : innerRy;
        const x = cx + rx * Math.cos(angle);
        const y = cy + ry * Math.sin(angle);
        path.push(i === 0 ? `M${x},${y}` : `L${x},${y}`);
    }
    path.push('Z');
    return path.join(' ');
}

/**
 * Convert a regular polygon to an SVG path
 * Uses separate X and Y radii to fill non-square bounding boxes
 */
function polygonToPath(cx: number, cy: number, rx: number, ry: number, sides: number): string {
    if (sides < 3) return '';
    const path: string[] = [];
    const angleStep = (2 * Math.PI) / sides;

    for (let i = 0; i < sides; i++) {
        const angle = i * angleStep - Math.PI / 2; // Start from top
        const x = cx + rx * Math.cos(angle);
        const y = cy + ry * Math.sin(angle);
        path.push(i === 0 ? `M${x},${y}` : `L${x},${y}`);
    }
    path.push('Z');
    return path.join(' ');
}

// ============================================================
// Shape Merging (for overlapping shapes with different operations)
// ============================================================

interface ParsedShapeWithSettings {
    shape: Omit<TemplateShape, 'id' | 'templateId'>;
    settings: ProcessSettingData[];
    originalOrderIndex: number;
}

/**
 * Merge overlapping shapes that have the same position/size but different operations.
 * In xTool Studio, multiple shapes may be stacked at the same position with different
 * operations (one for score, one for engrave, one for cut). In our template editor,
 * a single shape can have multiple operations, so we merge them.
 */
function mergeOverlappingShapes(
    shapesWithSettings: ParsedShapeWithSettings[]
): ParsedShapeWithSettings[] {
    const merged: ParsedShapeWithSettings[] = [];
    const tolerance = 0.1; // mm tolerance for position matching

    for (const item of shapesWithSettings) {
        const { shape, settings } = item;

        // Find existing shape at same position/size
        const existingIndex = merged.findIndex(m => {
            const s = m.shape;
            return (
                s.type === shape.type &&
                Math.abs(s.x - shape.x) < tolerance &&
                Math.abs(s.y - shape.y) < tolerance &&
                Math.abs(s.width - shape.width) < tolerance &&
                Math.abs(s.height - shape.height) < tolerance &&
                Math.abs((s.rotation || 0) - (shape.rotation || 0)) < tolerance
            );
        });

        if (existingIndex >= 0) {
            // Merge operations (OR the flags - only enable what's actually set)
            const existing = merged[existingIndex];
            existing.shape.score = existing.shape.score || shape.score;
            existing.shape.engrave = existing.shape.engrave || shape.engrave;
            existing.shape.cut = existing.shape.cut || shape.cut;

            // Preserve colors from the shape that has the operation enabled
            if (shape.score && shape.scoreColor) {
                existing.shape.scoreColor = shape.scoreColor;
            }
            if (shape.engrave && shape.engraveColor) {
                existing.shape.engraveColor = shape.engraveColor;
            }
            if (shape.cut && shape.cutColor) {
                existing.shape.cutColor = shape.cutColor;
            }

            // Merge SVG data if one is graphic (prefer the one with svgData)
            if (shape.svgData && !existing.shape.svgData) {
                existing.shape.svgData = shape.svgData;
                existing.shape.type = 'graphic';
            }

            // Merge process settings
            existing.settings.push(...settings);
        } else {
            // New shape - add to merged list
            merged.push({
                shape: { ...shape },
                settings: [...settings],
                originalOrderIndex: item.originalOrderIndex
            });
        }
    }

    // Re-assign orderIndex after merging
    return merged.map((item, index) => ({
        ...item,
        shape: { ...item.shape, orderIndex: index }
    }));
}

// ============================================================
// Process Settings Extraction
// ============================================================

/**
 * Extract process settings from XCS device data
 * Extracts ALL available fields for full round-trip fidelity
 */
function extractProcessSettings(
    shapeId: string,
    deviceDisplays: Map<string, XcsDeviceDisplay>
): ProcessSettingData[] {
    const display = deviceDisplays.get(shapeId);
    if (!display?.data) return [];

    const settings: ProcessSettingData[] = [];

    // Get processIgnore from display level (inverted to outputEnabled)
    // BUT only apply to the ACTIVE operation (matching processingType)
    // Other operations default to outputEnabled=true
    const activeProcessingType = display.processingType;
    const getOutputEnabled = (operationType: string) => {
        // Only the active operation inherits processIgnore state
        if (operationType === activeProcessingType) {
            return display.processIgnore !== true;
        }
        // Non-active operations default to enabled
        return true;
    };

    // Vector cutting = cut operation
    const vectorCutting = display.data.VECTOR_CUTTING?.parameter?.customize;
    if (vectorCutting) {
        settings.push({
            operation: 'cut',
            power: vectorCutting.power,
            speed: vectorCutting.speed,
            passes: vectorCutting.repeat,
            lightSource: vectorCutting.processingLightSource,
            outputEnabled: getOutputEnabled('VECTOR_CUTTING'),
            // Kerf compensation
            kerfEnabled: vectorCutting.enableKerf,
            kerfDistance: vectorCutting.kerfDistance,
            // Break points / tabs
            enableBreakPoint: vectorCutting.enableBreakPoint,
            breakPointSize: vectorCutting.breakPointSize,
            breakPointCount: vectorCutting.breakPointCount,
            breakPointMode: vectorCutting.breakPointMode,
            breakPointDistance: vectorCutting.breakPointDistance,
            breakPointPower: vectorCutting.breakPointPower,
        });
    }

    // Vector engraving = score operation
    const vectorEngraving = display.data.VECTOR_ENGRAVING?.parameter?.customize;
    if (vectorEngraving) {
        settings.push({
            operation: 'score',
            power: vectorEngraving.power,
            speed: vectorEngraving.speed,
            passes: vectorEngraving.repeat,
            lightSource: vectorEngraving.processingLightSource,
            outputEnabled: getOutputEnabled('VECTOR_ENGRAVING'),
            // Kerf compensation
            kerfEnabled: vectorEngraving.enableKerf,
            kerfDistance: vectorEngraving.kerfDistance,
        });
    }

    // Fill vector engraving = engrave operation
    const fillEngraving = display.data.FILL_VECTOR_ENGRAVING?.parameter?.customize;
    if (fillEngraving) {
        settings.push({
            operation: 'engrave',
            power: fillEngraving.power,
            speed: fillEngraving.speed,
            passes: fillEngraving.repeat,
            density: fillEngraving.density,
            scanMode: fillEngraving.bitmapScanMode,
            lightSource: fillEngraving.processingLightSource,
            outputEnabled: getOutputEnabled('FILL_VECTOR_ENGRAVING'),
            // Bitmap engraving settings
            bitmapEngraveMode: fillEngraving.bitmapEngraveMode,
            dotDuration: fillEngraving.dotDuration,
        });
    }

    // Bitmap engraving = engrave operation (for raster images)
    const bitmapEngraving = display.data.BITMAP_ENGRAVING?.parameter?.customize;
    if (bitmapEngraving) {
        settings.push({
            operation: 'engrave',
            power: bitmapEngraving.power,
            speed: bitmapEngraving.speed,
            passes: bitmapEngraving.repeat,
            density: bitmapEngraving.dpi,  // BITMAP uses 'dpi' instead of 'density'
            scanMode: bitmapEngraving.bitmapScanMode,
            lightSource: bitmapEngraving.processingLightSource,
            outputEnabled: getOutputEnabled('BITMAP_ENGRAVING'),
            // Bitmap-specific settings
            bitmapEngraveMode: bitmapEngraving.bitmapEngraveMode,
            dotDuration: bitmapEngraving.dotDuration,
        });
    }

    return settings;
}

// ============================================================
// Main Parse Function
// ============================================================

/**
 * Parse XCS file content into ImportResult
 *
 * XCS Coordinate System (matches our canvas):
 * - x,y for each shape is the TOP-LEFT corner
 * - Both XCS and our canvas use Y-down (screen coordinates)
 * - No coordinate flip is needed
 *
 * Import Strategy:
 * 1. Calculate the bounding box of all shapes
 * 2. Shift all shapes so the bounding box starts at (0,0)
 */
export async function parseXcsFile(
    content: string,
    workAreaWidth: number,
    workAreaHeight: number
): Promise<XcsImportResult> {
    const shapes: Omit<TemplateShape, 'id' | 'templateId'>[] = [];
    const warnings: string[] = [];
    const processSettings = new Map<number, ProcessSettingData[]>();

    let originalWidth = workAreaWidth;
    let originalHeight = workAreaHeight;
    let needsScaling = false;

    let xcsDeviceInfo: XcsDeviceInfo | undefined;

    try {
        const xcs: XcsFile = JSON.parse(content);

        // Extract device info for workarea mismatch warning
        const deviceId = xcs.device?.extId;
        if (deviceId) {
            const deviceWorkarea = XTOOL_DEVICE_WORKAREAS[deviceId];
            if (deviceWorkarea) {
                xcsDeviceInfo = {
                    deviceId,
                    deviceName: deviceWorkarea.name,
                    workAreaWidth: deviceWorkarea.width,
                    workAreaHeight: deviceWorkarea.height,
                };

                // Check for workarea mismatch (with 1mm tolerance)
                const widthMismatch = Math.abs(deviceWorkarea.width - workAreaWidth) > 1;
                const heightMismatch = Math.abs(deviceWorkarea.height - workAreaHeight) > 1;

                if (widthMismatch || heightMismatch) {
                    warnings.push(
                        `XCS file was created for ${deviceWorkarea.name} (${deviceWorkarea.width}x${deviceWorkarea.height}mm) ` +
                        `but current workarea is ${workAreaWidth}x${workAreaHeight}mm. Content may not fit correctly.`
                    );
                }
            } else {
                // Unknown device - store the ID anyway
                xcsDeviceInfo = { deviceId };
                warnings.push(`XCS file was created for unknown device "${deviceId}".`);
            }
        }

        // Get first canvas panel
        const canvas = xcs.canvas?.[0];
        if (!canvas?.displays || canvas.displays.length === 0) {
            warnings.push('No shapes found in XCS file');
            return {
                shapes: [],
                needsScaling: false,
                originalSize: { width: workAreaWidth, height: workAreaHeight },
                svgContent: '',
                warnings,
                xcsDeviceInfo,
            };
        }

        // Parse device displays to get processingType and processIgnore flags
        const deviceDisplays = parseDeviceDisplays(xcs.device);

        // First pass: Calculate the bounding box of all shapes
        // In xTool format, ALL shape types use:
        // - x,y as TOP-LEFT position
        // - width/height for dimensions (except TEXT which doesn't scale)
        // - LINE: x,y is offset, points define the shape relative to x,y
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

        for (const shape of canvas.displays) {
            const scaleX = shape.scale?.x ?? 1;
            const scaleY = shape.scale?.y ?? 1;

            let left: number, top: number, w: number, h: number;

            switch (shape.type) {
                case 'TEXT':
                    // TEXT: width/height are already final size (don't apply scale)
                    w = shape.width;
                    h = shape.height;
                    left = shape.x;
                    top = shape.y;
                    break;

                case 'CIRCLE': {
                    // CIRCLE: Handle edge case where scale is very small
                    // If unscaled is > 1mm but scaled would be < 1mm, don't apply scale
                    w = shape.width * scaleX;
                    h = shape.height * scaleY;
                    if (shape.width > 1 && w < 1) w = shape.width;
                    if (shape.height > 1 && h < 1) h = shape.height;
                    left = shape.x;
                    top = shape.y;
                    break;
                }

                case 'ELLIPSE':
                case 'STAR':
                case 'REGULAR_POLYGON':
                    // Other geometric shapes: x,y is TOP-LEFT, use width/height
                    w = shape.width * scaleX;
                    h = shape.height * scaleY;
                    left = shape.x;
                    top = shape.y;
                    break;

                case 'BITMAP':
                    // BITMAP width/height are ALREADY final display dimensions (scale is baked in)
                    w = shape.width;
                    h = shape.height;
                    left = shape.x ?? shape.left ?? 0;
                    top = shape.y ?? shape.top ?? 0;
                    break;

                case 'LINE': {
                    // LINE: Calculate bounding box from points
                    const points = shape.points;
                    if (!points || points.length === 0) continue;
                    const xs = points.map(p => p.x * scaleX);
                    const ys = points.map(p => p.y * scaleY);
                    const pMinX = Math.min(...xs);
                    const pMinY = Math.min(...ys);
                    const pMaxX = Math.max(...xs);
                    const pMaxY = Math.max(...ys);
                    w = pMaxX - pMinX || 1;
                    h = pMaxY - pMinY || 1;
                    left = shape.x + pMinX;
                    top = shape.y + pMinY;
                    break;
                }

                default:
                    // RECT, PATH: x,y is TOP-LEFT, apply scale to dimensions
                    w = shape.width * scaleX;
                    h = shape.height * scaleY;
                    left = shape.x;
                    top = shape.y;
            }

            const right = left + w;
            const bottom = top + h;

            minX = Math.min(minX, left);
            minY = Math.min(minY, top);
            maxX = Math.max(maxX, right);
            maxY = Math.max(maxY, bottom);
        }

        // Calculate content dimensions (use maxX/maxY since we preserve original positions)
        originalWidth = maxX;
        originalHeight = maxY;
        needsScaling = originalWidth > workAreaWidth || originalHeight > workAreaHeight;

        // Preserve original positions - don't shift content
        // The XCS file coordinates are already in the correct position for the workarea
        const offsetX = 0;
        const offsetY = 0;

        // Second pass: Parse each shape with coordinate offset
        // Collect shapes with their settings for merging
        const shapesWithSettings: ParsedShapeWithSettings[] = [];
        let orderIndex = 0;
        let skippedCount = 0;

        for (const xcsShape of canvas.displays) {
            // Get display settings for this shape
            const displaySettings = deviceDisplays.get(xcsShape.id);
            console.log('[LOOKUP] Shape type:', xcsShape.type, 'id:', xcsShape.id, 'found:', !!displaySettings, displaySettings ? { processingType: displaySettings.processingType, processIgnore: displaySettings.processIgnore } : 'UNDEFINED');

            let shape: Omit<TemplateShape, 'id' | 'templateId'> | null = null;

            switch (xcsShape.type) {
                case 'RECT':
                    shape = parseRectShape(xcsShape, orderIndex, displaySettings, offsetX, offsetY);
                    break;
                case 'PATH':
                    shape = parsePathShape(xcsShape, orderIndex, displaySettings, offsetX, offsetY);
                    break;
                case 'TEXT':
                    shape = parseTextShape(xcsShape, orderIndex, displaySettings, offsetX, offsetY);
                    break;
                case 'CIRCLE':
                    shape = parseCircleShape(xcsShape, orderIndex, displaySettings, offsetX, offsetY);
                    break;
                case 'ELLIPSE':
                    shape = parseEllipseShape(xcsShape, orderIndex, displaySettings, offsetX, offsetY);
                    break;
                case 'LINE':
                    shape = parseLineShape(xcsShape, orderIndex, displaySettings, offsetX, offsetY);
                    break;
                case 'STAR':
                    shape = parseStarShape(xcsShape, orderIndex, displaySettings, offsetX, offsetY);
                    break;
                case 'REGULAR_POLYGON':
                    shape = parsePolygonShape(xcsShape, orderIndex, displaySettings, offsetX, offsetY);
                    break;
                case 'BITMAP':
                    shape = parseBitmapShape(xcsShape, orderIndex, displaySettings, offsetX, offsetY);
                    break;
                default:
                    warnings.push(`Unsupported shape type: ${xcsShape.type}`);
                    skippedCount++;
                    continue;
            }

            if (shape) {
                // Extract process settings for this shape
                const settings = extractProcessSettings(xcsShape.id, deviceDisplays);

                shapesWithSettings.push({
                    shape,
                    settings,
                    originalOrderIndex: orderIndex
                });

                orderIndex++;
            } else {
                skippedCount++;
            }
        }

        // Merge overlapping shapes (same position/size but different operations)
        const mergedShapes = mergeOverlappingShapes(shapesWithSettings);

        // Extract final shapes and process settings from merged result
        for (const item of mergedShapes) {
            shapes.push(item.shape);
            if (item.settings.length > 0) {
                processSettings.set(item.shape.orderIndex, item.settings);
            }
        }

        if (skippedCount > 0) {
            warnings.push(`${skippedCount} shape(s) could not be imported`);
        }

        // Log merge summary if any shapes were merged
        const mergeCount = shapesWithSettings.length - mergedShapes.length;
        if (mergeCount > 0) {
            console.log(`[XCS Import] Merged ${mergeCount} overlapping shape(s)`);
        }

    } catch (error) {
        console.error('[XCS Import] Failed to parse XCS file:', error);
        warnings.push(`Failed to parse XCS file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    return {
        shapes,
        needsScaling,
        originalSize: { width: originalWidth, height: originalHeight },
        svgContent: '', // XCS doesn't have background SVG content
        processSettings,
        warnings,
        xcsDeviceInfo,
    };
}

/**
 * Scale shapes to fit within work area (reusable utility)
 */
export function scaleXcsShapesToFit(
    shapes: Omit<TemplateShape, 'id' | 'templateId'>[],
    originalWidth: number,
    originalHeight: number,
    targetWidth: number,
    targetHeight: number
): Omit<TemplateShape, 'id' | 'templateId'>[] {
    const scaleX = targetWidth / originalWidth;
    const scaleY = targetHeight / originalHeight;
    const scale = Math.min(scaleX, scaleY);

    return shapes.map(shape => ({
        ...shape,
        x: shape.x * scale,
        y: shape.y * scale,
        width: shape.width * scale,
        height: shape.height * scale,
    }));
}
